import { useEffect, useState } from "react";
import api from "@/lib/api";
import StudentLayout from "@/components/StudentLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Award, Lock, Printer, FileDown, Loader2 } from "lucide-react";

export default function StudentResults() {
  const { student } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dlKey, setDlKey] = useState("");
  const [dling, setDling] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/student/assessments");
        setData(data);
        // Report card = whatever the ADMIN last published to the parent on
        // WhatsApp. If nothing has been published yet, the button stays
        // disabled with a clear message — we don't "guess" a card the parent
        // was never shown.
        setDlKey(data.last_published_exam?.exam_key || "");
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
  }, []);

  const downloadCard = async () => {
    if (!dlKey) return;
    setDling(true);
    try {
      const res = await api.get("/student/report-card", { params: { exam_key: dlKey }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      // eslint-disable-next-line no-alert
    } finally { setDling(false); }
  };

  if (loading) return <StudentLayout><div className="text-slate-500">Loading…</div></StudentLayout>;
  if (!data) return <StudentLayout><div className="text-slate-500">Could not load results.</div></StudentLayout>;

  if (data.pre_primary) return <PrePrimaryResults data={data} student={student} />;

  const subjects = Object.entries(data.subjects || {});
  const columns = data.columns || [];
  const current = data.current_key;
  const curIdx = columns.findIndex((c) => c.key === current);

  return (
    <StudentLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4 print:hidden">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 mb-2">Student dashboard</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>My results</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Academic year {data.academic_year}</p>
        </div>
        {subjects.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              onClick={downloadCard}
              disabled={dling || !dlKey}
              data-testid="download-report-card-btn"
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60"
              title={dlKey ? `Published report card — ${data.last_published_exam?.exam_label || ""}` : "No report card has been published yet"}
            >
              {dling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}
              {dlKey
                ? `Report card (PDF) · ${data.last_published_exam?.exam_label || ""}`
                : "No published report card yet"}
            </Button>
          </div>
        )}
      </div>

      {/* Print-only header (only visible when printing) */}
      <div className="hidden print:block print:mb-6" data-testid="print-header">
        <div className="text-center border-b-2 border-slate-300 pb-4 mb-4">
          <div className="text-2xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Trishul High School</div>
          <div className="text-xs text-slate-500 mt-1">Davanagere · Karnataka</div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div><span className="text-slate-500 uppercase tracking-wider">Student</span><div className="font-semibold text-base">{student?.name}</div></div>
          <div><span className="text-slate-500 uppercase tracking-wider">Roll · Class</span><div className="font-semibold text-base">{student?.roll_number} · {student?.class_name}-{student?.section}</div></div>
          <div><span className="text-slate-500 uppercase tracking-wider">Academic year</span><div className="font-semibold text-base">{data.academic_year}</div></div>
        </div>
      </div>

      {subjects.length === 0 ? (
        <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-10 text-center" data-testid="results-empty">
          <Award className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-700 dark:text-slate-200 font-medium">No marks recorded yet.</div>
          <div className="text-xs text-slate-500 mt-1">Your teachers haven&apos;t entered marks for this academic year.</div>
        </div>
      ) : (
        <div className="space-y-6" data-testid="results-grid">
          {subjects.map(([subject, cells]) => {
            // Compute aggregate
            const known = columns
              .map((c) => ({ ...c, cell: cells[c.key] }))
              .filter((c) => c.cell?.marks != null && c.cell?.max_marks);
            const total = known.reduce((a, c) => a + c.cell.marks, 0);
            const max = known.reduce((a, c) => a + c.cell.max_marks, 0);
            const pct = max > 0 ? Math.round((total / max) * 100) : null;
            return (
              <div key={subject} className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-xl bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center">
                      <Award className="h-5 w-5" />
                    </div>
                    <div className="font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>{subject}</div>
                  </div>
                  {pct != null && (
                    <div className={`text-sm font-semibold px-3 py-1 rounded-full ${pct >= 75 ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : pct >= 50 ? "bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300" : "bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"}`}>
                      Aggregate {total}/{max} · {pct}%
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30">
                        {columns.map((col, idx) => {
                          const past = curIdx >= 0 && idx < curIdx;
                          const future = curIdx >= 0 && idx > curIdx;
                          const tone = future ? "text-slate-300 dark:text-slate-600" : past ? "text-slate-500 dark:text-slate-400" : "text-sky-700 dark:text-sky-300";
                          return (
                            <th key={col.key} className={`text-center px-3 py-2 text-[11px] font-semibold uppercase tracking-wide ${tone}`}>
                              <div className="flex items-center justify-center gap-1">
                                {future && <Lock className="h-3 w-3" />}
                                {col.short || col.label.replace("Monthly Test ", "MT ")}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {columns.map((col) => {
                          const c = cells[col.key];
                          return (
                            <td key={col.key} className="text-center px-3 py-3">
                              {c?.marks != null ? (
                                <div>
                                  <div className="font-semibold text-slate-900 dark:text-slate-100">{c.marks}</div>
                                  <div className="text-[10px] text-slate-400">/ {c.max_marks}</div>
                                </div>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-600">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </StudentLayout>
  );
}


const PP_GRADE_TONES = {
  "Excellent": "bg-emerald-100 text-emerald-700",
  "Very Good": "bg-teal-100 text-teal-700",
  "Good": "bg-sky-100 text-sky-700",
  "Fair": "bg-amber-100 text-amber-700",
  "Need Improvement": "bg-rose-100 text-rose-700",
};

function PrePrimaryResults({ data, student }) {
  const columns = data.columns || [];
  const subjects = Object.entries(data.subjects || {});
  const activities = Object.entries(data.activities || {});
  const hasAny = subjects.length > 0 || activities.length > 0;
  const [repExam, setRepExam] = useState((columns[0] || {}).key || "FA1");
  const [dl, setDl] = useState(false);

  const catTitle = (c) => {
    if (!c) return "";
    const parts = [];
    if (c.marks != null) parts.push(`Total: ${c.marks}/${c.max_marks || ""}`);
    if (c.categories) Object.entries(c.categories).forEach(([k, v]) => parts.push(`${k}: ${v}`));
    return parts.join("  ·  ");
  };

  const downloadReport = async () => {
    setDl(true);
    try {
      const res = await api.get(`/student/pre-primary/report-card`, { params: { exam_key: repExam }, responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = /filename\*?=(?:UTF-8'')?"?([^"';]+)"?/i.exec(cd);
      const fname = m ? decodeURIComponent(m[1]) : "progress-report.pdf";
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (_e) {
      // eslint-disable-next-line no-alert
    } finally { setDl(false); }
  };

  const Section = ({ title, rows }) => (
    <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700 font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30">
              <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Area</th>
              {columns.map((col) => (
                <th key={col.key} className="text-center px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-pink-700 dark:text-pink-300">{col.short}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([name, cells]) => (
              <tr key={name} className="border-b border-slate-100 dark:border-slate-800" data-testid={`pp-result-row-${name}`}>
                <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-200">{name}</td>
                {columns.map((col) => {
                  const c = cells[col.key];
                  const grade = c?.absent ? "Absent" : c?.grade;
                  const cats = c?.categories ? Object.entries(c.categories) : [];
                  return (
                    <td key={col.key} className="text-center px-3 py-3" title={catTitle(c)}>
                      {grade ? (
                        <>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${c?.absent ? "bg-rose-100 text-rose-600" : PP_GRADE_TONES[grade] || "bg-slate-100 text-slate-600"}`}>{grade}</span>
                          {c?.marks != null && <div className="text-[10px] text-slate-400 mt-1">{c.marks}/{c.max_marks}</div>}
                          {cats.length > 0 && (
                            <div className="text-[9px] text-slate-400 mt-0.5 leading-tight max-w-[120px] mx-auto">
                              {cats.map(([k, v]) => `${k.split(" ")[0]} ${v}`).join(" · ")}
                            </div>
                          )}
                        </>
                      ) : <span className="text-slate-300 dark:text-slate-600">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <StudentLayout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-pink-400 mb-2">Pre-Primary</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>My grade card</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Academic year {data.academic_year} · {student?.class_name}-{student?.section}</p>
        </div>
        {hasAny && (
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Report for</label>
              <select value={repExam} onChange={(e) => setRepExam(e.target.value)}
                className="h-10 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm" data-testid="pp-report-exam-select">
                {columns.map((c) => <option key={c.key} value={c.key}>{c.short}</option>)}
              </select>
            </div>
            <Button onClick={downloadReport} disabled={dl} className="bg-pink-600 hover:bg-pink-700 h-10" data-testid="pp-report-download-btn">
              {dl ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Progress Report
            </Button>
          </div>
        )}
      </div>
      {!hasAny ? (
        <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-10 text-center" data-testid="pp-results-empty">
          <Award className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-700 dark:text-slate-200 font-medium">No grades recorded yet.</div>
        </div>
      ) : (
        <div className="space-y-6" data-testid="pp-results-grid">
          {subjects.length > 0 && <Section title="Subjects" rows={subjects} />}
          {activities.length > 0 && <Section title="Activities" rows={activities} />}
        </div>
      )}
    </StudentLayout>
  );
}
